import React, { createContext, useContext, useEffect, useState } from 'react';
import jwt from 'jsonwebtoken';
import { useHistory } from 'react-router-dom';
import { gql, useApolloClient } from '@apollo/client';
import { isEqual } from 'lodash/fp';
import ReactGA from 'react-ga';
import { JWT_TOKEN_NAME } from '../../constants';
import { useSilentMutation } from '../../graphql/hooks';
import i18n from '../../intl';
import { Token } from '../../types';
import { Role } from '../../generated/graphql';
import { FullScreenLoader } from '../../ui-kit/FullScreenLoader';
import { routePaths } from '../../routePaths';

type DecodeResult = Token | null;

interface LoginArgs {
    token: string;
    referrer?: Location;
}

export const getUser = () => {
    const token = getToken();
    if (!token) {
        return null;
    }
    const user = decodeToken(token);
    if (!user) {
        return null;
    }
    return user;
};

export const getToken = () => localStorage.getItem(JWT_TOKEN_NAME);
export const decodeToken = (token: string) => jwt.decode(token) as DecodeResult;
interface ContextProps {
    user: null | Token;
    validate: () => void;
    logout: () => Promise<void>;
    login: (args: LoginArgs) => void;
}

/*
    Hopefully there is a better way to do this.
    Otherwise TS was yelling
 */

/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
const defaultState = {
    user: null,
    validate: () => {},
    logout: async () => { await Promise.resolve(); },
    login: (args: LoginArgs) => {},
};
/* eslint-enable @typescript-eslint/no-empty-function */
/* eslint-enable @typescript-eslint/no-unused-vars */

const AuthContext = createContext<ContextProps>(defaultState);

const CHECK_TOKEN = gql`
mutation validateToken($token: String!) {checkToken(token: $token)}
`;
interface ValidateMutationResult {
    checkToken: boolean;
}
export const ProvideAuth: React.FC = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [validateToken] = useSilentMutation<ValidateMutationResult>(CHECK_TOKEN);
    const auth = useProvideAuth();

    const localStorageListener = () => {
        const decodedAccessToken = getUser();
        const userLoggedOut = !!(!decodedAccessToken && auth.user);
        const userSwitchedVenue = !!(auth.user?.currentVenueId !== decodedAccessToken?.currentVenueId);
        if (userLoggedOut || userSwitchedVenue) {
            window.location.reload();
        }
    };

    useEffect(() => {
        window.addEventListener('storage', localStorageListener);
        return () => {
            window.removeEventListener('storage', localStorageListener);
        };
    // eslint-disable-next-line
    }, []);

    const check = async () => {
        const token = getToken();
        if (!token) {
            return Promise.reject();
        }
        /*
        * Legacy. Used to logout user with old token without user.role on it.
        * TODO: remove after implementing proper refresh token.
        */
        if (auth.user && !auth.user.role) {
            return auth.logout();
        }
        try {
            const { data } = await validateToken({ variables: { token } });
            if (data) {
                if (auth.user) {
                    i18n.changeLanguage(auth.user.languageCode).catch(e => console.error(e));
                }

                return data.checkToken;
            }

            return false;
        } catch (error) {
            // nothing we can do
            return false;
        }
    };
    useEffect(() => {
        check()
            .then(async result => {
                if (!result) {
                    await auth.logout();
                }
            })
            .catch(error => {
                // nothing we can do
                console.error(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    // eslint-disable-next-line
    }, []);

    return (
        <AuthContext.Provider value={auth}>
            {isLoading
            ? (
                <FullScreenLoader />
            ) : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};


// Provider hook that creates auth object and handles state
function useProvideAuth() {
    const [user, setUser] = useState<DecodeResult>(getUser());
    const history = useHistory();
    const validate = () => {
        const decoded = getUser();
        if (!isEqual(decoded, user)) {
            setUser(decoded);
        }
        if (decoded) {
            ReactGA.set({ userId: decoded.employeeId });
            if (decoded.languageCode !== i18n.language) {
                // eslint-disable-next-line no-void
                void i18n.changeLanguage(decoded.languageCode);
            }
            return true;
        }
        history.push(routePaths.login);
        return false;
    };
    const client = useApolloClient();
    const logout = async (): Promise<void> => {
        localStorage.removeItem(JWT_TOKEN_NAME);
        setUser(null);
        history.replace(routePaths.login);
        await client.clearStore();
    };

    const login = ({ token, referrer }: LoginArgs): void => {
        localStorage.setItem(JWT_TOKEN_NAME, token);
        validate();
        if (!referrer) {
            history.go(0);
            return;
        }
        history.push(referrer);
    };

    useEffect(() => {
        validate();
        // eslint-disable-next-line
    }, []);

    return {
        user,
        validate,
        login,
        logout,
    };
}

export const tokenTypeGuard = (token: DecodeResult): token is Token => {
    return token !== null;
};

export const useUser = () => {
    const { user, logout } = useProvideAuth();
    if (!tokenTypeGuard(user)) {
        logout().catch(e => console.error(e));
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const guaranteedUser = user as Token;
    const isAdmin = guaranteedUser.role === Role.Admin;
    return { user: guaranteedUser, isAdmin };

};
